import type { Role } from "@imagora/contracts";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { roleAtLeast } from "../nav";
import { queryKeys } from "./query-keys";
import { api } from "./runtime";
import type { Session } from "./session";

export const SessionContext = createContext<Session | null>(null);

/** Only for what renders inside the shell, which is shown only with a session. */
export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession outside a session");
  return session;
}

export function useCan(required: Role): boolean {
  return roleAtLeast(useSession().role, required);
}

/** The tenant as it is now: the exchange's copy until the API answers, then kept fresh by edits. */
export function useTenant() {
  const session = useSession();
  return useQuery({ queryKey: queryKeys.tenant, queryFn: () => api.tenant.get(), initialData: session.tenant, staleTime: 60_000 }).data;
}

export interface Brand {
  productName: string;
  logo: string | null;
  /** Whether people sign up and sign in on this address, rather than arriving from a host's own login. */
  directAccounts: boolean;
}

export const BrandContext = createContext<Brand>({ productName: "", logo: null, directAccounts: false });

export const useBrand = () => useContext(BrandContext);
